import { Component, OnInit, signal, ViewChild } from '@angular/core';
import { OT } from '../../../../shared/models/agenda/ot.model';
import { AlertController, IonicModule, ModalController } from '@ionic/angular';
import { LocalisationsProvider } from '../../../../shared/providers/localisations/localisations';
import { PortefeuilleService } from '../../../../shared/providers/portefeuille/portefeuille.service';
import { UserService } from '../../../../shared/providers/auth/user.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NetworkProvider } from '../../../../shared/providers/network/network';
import { ConnectionStatus, Network } from '@capacitor/network';
import moment from 'moment';
import { AgendaService } from '../../../../shared/providers/agenda/agenda.service';
import { first, firstValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import { ByDayCollection } from '../../../../shared/models/collections.model';
import { AgendaJour } from '../../../../shared/models/agenda/agenda-jour.model';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { OtCardComponent } from '../../../../shared/components/ot-card/ot-card.component';
import { GetSitePortefeuilleResponseDto } from '../../../../api/models/get-site-portefeuille-response-dto';
import { LoggerFactory } from '../../../../shared/providers/logger/logger-factory.service';
import { LoggerWrapper } from '../../../../shared/providers/logger/logger-wrapper.service';
import { EquipementDto } from '../../../../api/models/equipement-dto';
import { ScanEquipementComponent } from '../../../../shared/components/scan-equipement/scan-equipement.component';
import { isTogglePortefeuillePerimetreIsoleEnabled } from '../../../../shared/utils/toggle-utils';

@Component({
  selector: 'recherche-site-ot-modal',
  templateUrl: 'recherche-site-ot.modal.html',
  styleUrls: ['./recherche-site-ot.modal.scss'],
  standalone: true,
  imports: [IonicModule, CommonModule, FormsModule, TranslateModule, OtCardComponent, ScanEquipementComponent],
})
export class RechercheSiteOtModal implements OnInit {
  /**
   * Id de l'utilisateur connecté
   */
  private readonly userId: string;

  /**
   * Liste des OTs du technicien
   */
  ots: OT[];

  recherche_elargie = false;

  agendaJours: ByDayCollection<AgendaJour>;

  search_value = '';

  /**
   * Liste des OTs en réponse à la recherche
   */
  filteredOts: OT[] = [];

  /**
   * Sites du technicien
   */
  sites: GetSitePortefeuilleResponseDto[];

  /**
   * Liste des sites en réponse à la recherche
   */
  filteredSites: GetSitePortefeuilleResponseDto[] = [];

  /**
   * Texte saisi par l'utilisateur
   */
  searchText: string;

  private logger: LoggerWrapper;

  loading_sites = signal<boolean>(false);
  loading_ots = signal<boolean>(false);
  isConnected = signal<boolean>(true);

  @ViewChild('searchInput') searchInput;
  /**
   * constructeur de la modale
   */
  constructor(
    private viewCtrl: ModalController,
    private localisationsProvider: LocalisationsProvider,
    private portefeuilleService: PortefeuilleService,
    private userService: UserService,
    private agendaService: AgendaService,
    private alertCtrl: AlertController,
    private translate: TranslateService,
    private networkProvider: NetworkProvider,
    private router: Router,
    private loggerFactory: LoggerFactory
  ) {
    this.userId = this.userService.getId();
    this.logger = this.loggerFactory.getInstance(this, false);
  }

  /**
   * Fonction appelée à l'initialisation du composant
   */
  async ngOnInit() {
    this.isConnected.set(await this.networkProvider.isReallyConnected());

    Network.addListener('networkStatusChange', (status: ConnectionStatus) => {
      this.isConnected.set(status.connected);
    });
    await this.refresh();

    setTimeout(() => {
      this.searchInput.setFocus();
    }, 300);
  }

  async refresh() {
    try {
      this.loading_ots.set(true);
      if (this.agendaJours == null) {
        this.agendaJours = await firstValueFrom(this.agendaService.getAgenda(new Date(), false).pipe(first()));
        this.sortOts(this.agendaJours);
      } else {
        this.sortOts(this.agendaJours);
      }
    } catch (error) {
      this.logger.error(error, 'Une erreur est survenue lors de la récupération des OTs pour la recherche');
    } finally {
      this.loading_ots.set(false);
    }
  }

  sortOts(agendaJours: ByDayCollection<AgendaJour>) {
    this.ots =
      (agendaJours &&
        agendaJours
          .getValues()
          .reduce((ots, jour) => ots.concat(jour.ots), [])
          .sort((a: OT, b: OT) => b.dateDebut.getTime() - a.dateDebut.getTime())) ||
      [];
  }

  async rechercheElargie() {
    let searchText = this.search_value.trim().toLocaleLowerCase();
    if (searchText.length < 3) {
      const alert = await this.alertCtrl.create({
        header: this.translate.instant('Erreur'),
        message: 'Il faut saisir au minimum 3 caractères pour effectuer une recherche élargie',
        buttons: [this.translate.instant('Fermer')],
      });
      await alert.present();
    } else {
      this.recherche_elargie = true;
      this.loading_sites.set(true);
      this.filteredSites = [];
      let result_sites = await this.localisationsProvider.searchElargie(searchText);
      this.filteredSites = result_sites.slice(0, 50);
      this.loading_sites.set(false);
    }
  }

  /**
   * Construit les listes filtrées de sites et d'OTs
   * @param inputEvent Evenement de la searchbar
   */
  async filterData(inputEvent) {
    // this.content.resize();
    if (!this.sites) {
      this.loading_sites.set(true);
      this.sites = await this.portefeuilleService.getPortefeuilleOrPresentAlert(this.userId);
      this.sites.sort((a, b) => a.libelle_long && a.libelle_long.localeCompare(b.libelle_long));
      this.loading_sites.set(false);
    }
    this.searchText = inputEvent.target.value ? inputEvent.target.value.trim().toLocaleLowerCase() : null;

    if (this.searchText && this.searchText.length >= 3) {
      if (this.recherche_elargie) {
        await this.rechercheElargie();
      } else {
        this.filteredSites = this.sites
          .filter(
            (site) =>
              (site.libelle_long && site.libelle_long.toLocaleLowerCase().indexOf(this.searchText) > -1) ||
              (site.code_localisation && site.code_localisation.toLocaleLowerCase().indexOf(this.searchText) > -1)
          )
          .slice(0, 10);
        console.log('this.filteredSites', this.filteredSites);
      }

      this.filteredOts = this.ots
        .filter(
          (ot) =>
            (ot.libelle && ot.libelle.toLocaleLowerCase().indexOf(this.searchText) > -1) ||
            (ot.numero && ot.numero.toLocaleLowerCase().indexOf(this.searchText) > -1) ||
            (ot.site &&
              ((ot.site.libelle_long && ot.site.libelle_long.toLocaleLowerCase().indexOf(this.searchText) > -1) ||
                (ot.site.code_localisation &&
                  ot.site.code_localisation.toLocaleLowerCase().indexOf(this.searchText) > -1)))
        )
        .filter((ot, i, arr) => arr.findIndex((t) => ot.id === t.id) === i)
        .sort((a, b) => {
          return moment.utc(b.dateDebut).diff(moment.utc(a.dateDebut));
        })
        .slice(0, 10);
    } else {
      this.recherche_elargie = false;
      this.filteredSites = [];
      this.filteredOts = [];
    }
  }

  /**
   * Redirige vers la page de détail de l'OT sélectionné
   * @param ot OT sélectionné
   */
  async openDetailOt(ot: OT) {
    if (ot.site) {
      this.viewCtrl.dismiss().then(async () => {
        await this.router.navigate(['/mnt/ots/', ot.id], {
          queryParams: {
            siteId: ot.site.id,
            team: false,
          },
        });
      });
    } else {
      const alert = await this.alertCtrl.create({
        header: this.translate.instant('Erreur'),
        message: this.translate.instant("Données de l'OT manquantes"),
        buttons: [this.translate.instant('Fermer')],
      });
      await alert.present();
    }
  }

  /**
   * Redirige vers la page de détail du site sélectionné
   * @param site Site sélectionné
   */
  async openDetailsSite(site: GetSitePortefeuilleResponseDto) {
    this.viewCtrl.dismiss().then(async () => {
      await this.router.navigate(['/mnt/sites/', site.id_site]);
    });
  }

  /**
   * Ferme la modale
   */
  close() {
    if (this.search_value.length > 0) {
      this.search_value = '';
    } else {
      this.viewCtrl.dismiss();
    }
  }

  dayRoundsTrackBy(index: number, item: GetSitePortefeuilleResponseDto) {
    return item.id_site;
  }

  async onScannedEquipement(equipement: EquipementDto) {
    this.viewCtrl.dismiss().then(async () => {
      await this.router.navigate(['/mnt/equipement', equipement.id]);
    });
  }
  isSuperAdmin(): boolean {
    return this.userService.isSuperAdmin();
  }

  protected readonly isTogglePortefeuillePerimetreIsoleEnabled = isTogglePortefeuillePerimetreIsoleEnabled;
}
